import { Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ConfigService } from '../../services/config.service';
import { TopBar } from '../../models/client-configuration';
import { BroadcastService } from '../../services/broadcast.service';
import { AuthService } from '../../services/auth.service';
import { HeaderService } from './services/header.service';
import { MatMenu } from '@angular/material/menu';
import { fromEvent, map, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ThemeService } from '../../services/theme.service';
import { Router } from '@angular/router';
import { UserEventsService } from '../../services/user-events/user-events.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnDestroy, OnInit {
  private eventsBadgeSubscription: Subscription;
  private eventsAllReadSubscription: Subscription;
  private headerLogosSubscription: Subscription;
  private headerLogosAltSubscription: Subscription;
  private notificationsBadgeSubscription: Subscription;
  private notificationsAllReadSubscription: Subscription;

  @HostBinding('class.top-bar') get headerTobBar() {
    return this.topBar.show;
  }

  @HostBinding('class.banner-logos') get headerBannerLogos() {
    return this.headerLogos.length > 0;
  }

  @ViewChild('helpMenu') helpMenu!: MatMenu;

  applyShadow$!: Observable<boolean>;
  bookClubName: string;
  headerLogos: string[] = [];
  headerLogosAlt: string[] = [];
  isAulaMadrid: boolean = false;
  isEventsEnabled: boolean = false;
  isGuestLoginEnabled: boolean = false;
  isUserAuthenticated: boolean = false;
  isSignUpEnabled: boolean = false;
  isBookClubEnabled: boolean = false;
  logoLink!: string;
  logoLinkTarget: string;
  logoUrl: string;
  sidebarOpen$: Observable<boolean>;
  topBar: TopBar;

  constructor(
    private readonly authService: AuthService,
    private readonly broadcastService: BroadcastService,
    private readonly configService: ConfigService,
    private readonly headerService: HeaderService,
    private readonly router: Router,
    private readonly themeService: ThemeService,
    private readonly userEventsService: UserEventsService,
  ) { }

  ngOnInit(): void {
    this.bookClubName = this.configService.get().bookClubName || 'tooltip.bookClubName';
    this.headerLogosSubscription = this.themeService.headerLogos$.subscribe((logos) => this.headerLogos = logos);
    this.headerLogosAltSubscription = this.themeService.headerLogosAlt$.subscribe((logosAlt) => this.headerLogosAlt = logosAlt);
    this.isAulaMadrid = this.configService.get().codCliente === 'E1155';
    this.isEventsEnabled = this.configService.get().showEventsSection;

    this.isGuestLoginEnabled = this.configService.get().inviteGuestAccountActive && this.configService.get().customLoginUrl !== null;
    this.isUserAuthenticated = this.authService.isAuthenticated() ?? false;
    this.isSignUpEnabled = this.configService.get().allowSignUp;

    this.isBookClubEnabled = this.configService.get().showBookClubButton;

    const [logoLink, logoTarget] = this.getLogoConfig();
    this.logoLink = logoLink;
    this.logoLinkTarget = logoTarget;
    this.logoUrl = this.themeService.logoUrl;

    this.topBar = this.configService.get().topBar;
    if (this.configService.get().consortia) {
      this.topBar.show = !this.isUserAuthenticated;
    }

    this.applyShadow$ = fromEvent(window, 'scroll')
      .pipe(
        map(() => {
          const scrollPosition = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
          return scrollPosition > 20;
        }),
        distinctUntilChanged()
      );

    this.sidebarOpen$ = this.broadcastService.sidebar$;

    // TODO probably should move this away to a more specific place, maybe to home or layout initialization?
    if (this.isUserAuthenticated && this.configService.get().showEventsSection) {
      this.userEventsService.userEventEndpoint().subscribe();
    }
  }

  ngOnDestroy() {
    this.eventsAllReadSubscription?.unsubscribe();
    this.eventsBadgeSubscription?.unsubscribe();
    this.headerLogosSubscription?.unsubscribe();
    this.headerLogosAltSubscription?.unsubscribe();
    this.notificationsAllReadSubscription?.unsubscribe();
    this.notificationsBadgeSubscription?.unsubscribe();
  }

  handleBookClubClick() {
    this.headerService.openBookClub();
  }

  handleLogin() {
    this.headerService.login();
  }

  handleGuestLogin() {
    this.headerService.guestLogin();
  }

  handleSignUp() {
    this.headerService.signUp();
  }

  handleSearchButton() {
    this.broadcastService.broadcastSidebar(true);
  }

  navigateHome() {
    this.router.navigate(['/']);
  }

  get logoAltTarget(): string {
    return this.configService.get()?.logoUrl;
  }

  private getLogoConfig(): [string, string] {
    const logoUrl = this.configService.get()?.logoUrl;

    if (logoUrl) {
      return [logoUrl, '_blank'];
    }

    return ['/', '_self'];
  }
}
